/**
 * Editorial Memory — a read-only view of the Targeting Strategist's editorial
 * decision corpus: every override (redirect) and confirmation (accept), newest
 * first. This is the raw material the strategist will learn from once outcomes
 * are graded. Outcomes show "pending" until the resulting articles mature.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { blogRunsService, EditorialPrecedent } from '../../../../services/blogRunsService';
import { DataTable, DataTableColumn } from '../../shared/DataTable';

const ACTION_LABEL: Record<string, string> = {
  override_pick: 'Redirected → analyzed candidate',
  override_new: 'Redirected → new keyword',
  confirm: 'Confirmed',
  dialogue: 'Discussed',
};

function ActionBadge({ action }: { action: string }) {
  const isConfirm = action === 'confirm';
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full ${
        isConfirm ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'
      }`}
    >
      {ACTION_LABEL[action] || action}
    </span>
  );
}

function OutcomeBadge({ outcome }: { outcome?: EditorialPrecedent['outcome'] }) {
  const status = outcome?.status || 'pending';
  const styles: Record<string, string> = {
    validated: 'bg-green-100 text-green-700',
    flopped: 'bg-red-100 text-red-700',
    mixed: 'bg-amber-100 text-amber-700',
    pending: 'bg-gray-100 text-gray-500',
  };
  return <span className={`text-xs px-2 py-0.5 rounded ${styles[status] || styles.pending}`}>{status}</span>;
}

type Row = EditorialPrecedent & { key: string };

export function EditorialMemorySection() {
  const [precedents, setPrecedents] = useState<EditorialPrecedent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { precedents } = await blogRunsService.listEditorialPrecedents();
      setPrecedents(precedents || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const rows: Row[] = useMemo(
    () => precedents.map((p) => ({ ...p, key: p.record_id })),
    [precedents]
  );

  const fmtDate = (s: string) => {
    try {
      return new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return s?.slice(0, 10) || '';
    }
  };

  const columns: DataTableColumn<Row>[] = [
    { key: 'date', header: 'Date', className: 'whitespace-nowrap text-gray-500', render: (r) => fmtDate(r.created_at) },
    {
      key: 'topic',
      header: 'Topic',
      render: (r) => (
        <div>
          <div className="text-gray-800">{r.topic || '—'}</div>
          {r.target?.content_pillar && <div className="text-xs text-gray-400">{r.target.content_pillar}</div>}
        </div>
      ),
    },
    { key: 'action', header: 'Decision', render: (r) => <ActionBadge action={r.human?.action} /> },
    {
      key: 'target',
      header: 'Target keyword',
      render: (r) => (
        <div>
          <div className="text-gray-800">{r.human?.keyword || r.target?.primary_keyword || '—'}</div>
          {(r.target?.seo_score != null || r.target?.aeo_score != null) && (
            <div className="text-xs text-gray-400">
              {r.target?.seo_score != null && <>SEO {r.target.seo_score} </>}
              {r.target?.aeo_score != null && <>AEO {r.target.aeo_score}</>}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'strategist',
      header: 'Strategist said',
      render: (r) => <span className="text-xs text-gray-600">{r.strategist?.verdict || '—'}</span>,
    },
    { key: 'outcome', header: 'Outcome', render: (r) => <OutcomeBadge outcome={r.outcome} /> },
    { key: 'author', header: 'By', className: 'text-gray-500 text-xs', render: (r) => r.human?.author || '—' },
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Editorial Memory</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Every time you redirect or confirm the Targeting Strategist, it's recorded here — the corpus the
            strategist will learn from once outcomes are graded (weighted by what actually performed, not by how
            often it was overridden). Click a row to see the reasoning. Outcomes read "pending" until the
            resulting articles mature.
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 text-gray-600"
        >
          Refresh
        </button>
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <>
          <div className="text-xs text-gray-400">{precedents.length} decision{precedents.length !== 1 ? 's' : ''} recorded</div>
          <DataTable
            columns={columns}
            rows={rows}
            emptyMessage="No editorial decisions yet — redirect or accept a strategist call and it'll appear here."
            onRowClick={(r) => setExpanded(expanded === r.record_id ? null : r.record_id)}
            renderExpanded={(r) =>
              r.record_id !== expanded ? null : (
                <div className="space-y-3 text-sm">
                  {r.human?.rationale && (
                    <div>
                      <div className="text-xs font-medium text-gray-500">Human rationale</div>
                      <div className="text-gray-700 whitespace-pre-wrap">{r.human.rationale}</div>
                    </div>
                  )}
                  {r.strategist?.reasoning && (
                    <div>
                      <div className="text-xs font-medium text-gray-500">Strategist reasoning at the time</div>
                      <div className="text-gray-600 whitespace-pre-wrap border-l-2 border-gray-200 pl-2">{r.strategist.reasoning}</div>
                    </div>
                  )}
                  {!r.human?.rationale && !r.strategist?.reasoning && (
                    <div className="text-gray-400">No recorded reasoning for this decision.</div>
                  )}
                  <div className="text-xs text-gray-400">run {r.run_id} · {r.type}</div>
                </div>
              )
            }
          />
        </>
      )}
    </div>
  );
}
